import React, {Dispatch, ReactNode, SetStateAction, useContext, useState} from "react";
import {ProductsAcronymsEnum} from "../../models/Products/ProductsAcronymsEnum";
import {useSimulation} from "./SimulationProvider";
import {useAuth} from "../AuthProvider";
import SimulationService from "../../services/SimulationService";
import {useFinancial} from "../FinancialProvider";

interface ContextProps {
    listCclt: any[],
    setListCclt: Dispatch<SetStateAction<any[]>>,
    listFilterCclt: any[],
    setListFilterCclt: Dispatch<SetStateAction<any[]>>,
    listFinancialsProposalsCclt: string[],
    setListFinancialsProposalsCclt: Dispatch<SetStateAction<string[]>>,
    valueMinimumCclt: number,
    installmentsCclt: number,
    setInstallmentsCclt: Dispatch<SetStateAction<number>>,
    valueCclt: number,
    setValueCclt: Dispatch<SetStateAction<number>>,
    valueMaximumCclt: number,
    setValueMaximumCclt: Dispatch<SetStateAction<number>>,
    installmentsPossibleCclt: number[],
    setInstallmentsPossibleCclt: Dispatch<SetStateAction<number[]>>,
    installmentsPossibleValuesCclt: any[],
    setInstallmentsPossibleValuesCclt: Dispatch<SetStateAction<any[]>>,
    simulateCclt: (filter: boolean) => Promise<any>,
    simulateByFinancialCclt: (financial: string, value: number, installments: number, confirmation?: boolean) => Promise<any>,
    retrySimulate: (financial: string, value: number, installments: number, type: 'first' | 'filter' | 'preview', index: number) => void,
    removeItemCclt: (index: number) => void,
}

export const SimulationCcltContext = React.createContext<ContextProps>({} as ContextProps);

interface SimulationCcltProviderProps {
    children: ReactNode,
}

export const SimulationCcltProvider = ({children}: SimulationCcltProviderProps) => {
    const [listCclt, setListCclt] = useState<any[]>([]);
    const [listFilterCclt, setListFilterCclt] = useState<any[]>([]);
    const [installmentsCclt, setInstallmentsCclt] = useState<number>(0);
    const [valueCclt, setValueCclt] = useState<number>(0);
    const [valueMinimumCclt] = useState<number>(200);
    const [valueMaximumCclt, setValueMaximumCclt] = useState<number>(0);
    const [installmentsPossibleCclt, setInstallmentsPossibleCclt] = useState<number[]>([]);
    const [listFinancialsProposalsCclt, setListFinancialsProposalsCclt] = useState<string[]>([]);
    const [installmentsPossibleValuesCclt, setInstallmentsPossibleValuesCclt] = useState<any[]>([]);

    const {setIsLoadingCard, setListPreview, sortPossibleInstallments} = useSimulation();
    const {userSimulation} = useAuth();
    const {financialsCclt} = useFinancial();
    const simulationService = new SimulationService();
    const fakeParam = process.env.NODE_ENV === 'development' ? {fake: 'fake'} : {};

    const orderBySimulate = (a: any, b: any) => {
        if (a?.data?._financeira > b?.data?._financeira) return -1;
        if (a?.data?._financeira < b?.data?._financeira) return 1;
        if (a?.data?.valor > b?.data?.valor) return -1;
        if (a?.data?.valor < b?.data?.valor) return 1;
        if (a?.data?.prazo > b?.data?.prazo) return -1;
        if (a?.data?.prazo < b?.data?.prazo) return 1;
        return 1;
    };

    const listSetterByType: Record<string, Dispatch<SetStateAction<any[]>>> = {
        first: setListCclt,
        filter: setListFilterCclt,
        preview: setListPreview,
    };

    const retrySimulate = async (
        financial: string,
        value: number,
        installments: number,
        type: 'first' | 'filter' | 'preview',
        index: number
    ) => {
        const [_Response] = await simulationService.simulate({
            _financeira: financial,
            _produto: ProductsAcronymsEnum.CCLT,
            cpf: userSimulation?.cpf,
            cellphone: userSimulation?.cellphone,
            birthday: userSimulation?.birthday,
            name: userSimulation?.name,
            email: userSimulation?.email,
            uf: userSimulation?.uf || '',
            value: value || valueCclt || valueMinimumCclt,
            installments: installments || installmentsCclt || 24,
            confirmation: type === 'preview',
            saleOriginId: userSimulation?.saleOriginId,
            typistId: userSimulation?.typist,
            ...fakeParam,
        });

        if (_Response?.data) {
            _Response.data._produto = ProductsAcronymsEnum.CCLT;
        }

        listSetterByType[type]((current) => {
            current[index] = _Response;
            return [...current];
        });
    };

    // Guard clause: handle filter path first, then return — removes the else branch
    const resultSimulate = (filter: boolean, item: any) => {
        if (filter) {
            const listSort = [...listFilterCclt, item].sort(orderBySimulate);
            setListFilterCclt(
                listSort.filter((value, index, self) =>
                        index === self.findIndex((t) =>
                            t?.data?._financeira === value?.data?._financeira &&
                            t?.data?.financedAmount === value?.data?.financedAmount &&
                            t?.data?.installments === value?.data?.installments
                        )
                )
            );
            setIsLoadingCard('');
            return;
        }

        if (!item?.data?.fake && item?.data?.installmentConditions?.length > 0) {
            setInstallmentsPossibleValuesCclt(item?.data?.installmentConditions);
            sortPossibleInstallments(
                item?.data?.installmentConditions?.map((x: any) => x.installment),
                installmentsPossibleCclt,
                setInstallmentsPossibleCclt
            );
        }

        setValueCclt(item?.data?.valor || valueMinimumCclt);
        setListCclt((current: any) => [...current, item]);
    };

    const simulateCclt = (filter = false) => {
        if (filter) {
            setIsLoadingCard(ProductsAcronymsEnum.CCLT);
        }

        return new Promise((resolve, reject) => {
            const listP = (financialsCclt || []).map((x: any) => {
                // Guard clause: skip financials that already have a proposal
                if (listFinancialsProposalsCclt.indexOf(x?.sigla.toLowerCase()) !== -1) {
                    return [];
                }

                return simulationService.simulate({
                    _financeira: x?.sigla.toLowerCase(),
                    _produto: ProductsAcronymsEnum.CCLT,
                    cpf: userSimulation?.cpf,
                    cellphone: userSimulation?.cellphone,
                    birthday: userSimulation?.birthday,
                    name: userSimulation?.name,
                    email: userSimulation?.email,
                    uf: userSimulation?.uf || '',
                    value: valueCclt || valueMinimumCclt,
                    installments: installmentsCclt || 24,
                    saleOriginId: userSimulation?.saleOriginId,
                    typistId: userSimulation?.typist,
                    ...fakeParam,
                });
            });

            Promise.all(listP)
                .then((lista) => {
                    lista?.forEach((item: any[]) => {
                        // Guard clause: skip empty results
                        if (!item?.length) return;

                        resultSimulate(filter, item[0]);

                        if (parseInt(item[0]?.data?.propostaId) > 0) {
                            setListFinancialsProposalsCclt((current): string[] => [
                                ...current,
                                item[0]?.data?._financeira.toLowerCase() as string,
                            ]);
                        }
                    });
                    resolve(lista);
                })
                .catch((erro) => reject(erro));
        });
    };

    const simulateByFinancialCclt = (
        financial: string,
        value: number,
        installments: number,
        confirmation = false
    ) => {
        return new Promise(async (resolve) => {
            const [_Response] = await simulationService.simulate({
                _financeira: financial,
                _produto: ProductsAcronymsEnum.CCLT,
                cpf: userSimulation?.cpf,
                cellphone: userSimulation?.cellphone,
                birthday: userSimulation?.birthday,
                name: userSimulation?.name,
                email: userSimulation?.email,
                uf: userSimulation?.uf || '',
                confirmation,
                value: value || valueMinimumCclt,
                installments,
                saleOriginId: userSimulation?.saleOriginId,
                typistId: userSimulation?.typist,
            });

            if (_Response?.data) {
                _Response.data._produto = ProductsAcronymsEnum.CCLT;
            }

            setListPreview((current: any) => [...current, _Response]);
            resolve(_Response);
        });
    };

    // Guard clause: bail early on invalid index
    const removeItemCclt = (index: number) => {
        if (index === -1) return;
        const array = [...listFilterCclt];
        array.splice(index, 1);
        setListFilterCclt(array);
    };

    return (
        <SimulationCcltContext.Provider
            value={{
                listCclt,
                setListCclt,
                listFilterCclt,
                setListFilterCclt,
                valueMinimumCclt,
                installmentsCclt,
                setInstallmentsCclt,
                valueCclt,
                setValueCclt,
                valueMaximumCclt,
                setValueMaximumCclt,
                installmentsPossibleCclt,
                setInstallmentsPossibleCclt,
                listFinancialsProposalsCclt,
                setListFinancialsProposalsCclt,
                installmentsPossibleValuesCclt,
                setInstallmentsPossibleValuesCclt,
                simulateCclt,
                simulateByFinancialCclt,
                retrySimulate,
                removeItemCclt,
            }}
        >
            {children}
        </SimulationCcltContext.Provider>
    );
};

export const useSimulationCclt = () => useContext(SimulationCcltContext);

